/**
 * Selling an animal (P3-BR1, BR-15, BR-16).
 *
 * This sits under the costs tab rather than on the overview, because the number
 * that should be in front of a partner when they set a price is what the animal
 * has already cost the farm: purchase, its share of the trip that bought it,
 * health and feed. BR-16 exists so that figure can be known per animal, and
 * this is the moment it is worth knowing.
 *
 * Two things happen on save and both are worth understanding before changing
 * anything here. The animal leaves the herd, and a *pending* income entry is
 * created (BR-07). The animal leaves on the strength of the record rather than
 * the approval — so if another partner rejects or voids that entry, the service
 * puts the animal back (P3-BR1). Nothing in this form needs to handle that, but
 * a partner reading "sold" on a herd list is reading an unapproved fact.
 */

import { toMoney, toMoneyString } from '@agrofarm/shared';
import { useState, type FormEvent } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorFields, errorMessageKey } from '../../lib/apiError.js';
import { formatMoney, isNegativeAmount } from '../../lib/currency.js';
import { todayInDhaka } from '../../lib/date.js';
import { useAccounts } from '../finance/api.js';
import { useCustomers } from '../sales/api.js';
import { useRecordAnimalSale } from './api.js';

/** Digits with at most two decimals, checked as text — never parsed (MONEY-1). */
const DECIMAL_DRAFT = /^\d{1,12}(\.\d{1,2})?$/;

/**
 * What the farm makes or loses on this animal.
 *
 * `toMoney` from `@agrofarm/shared` — the same Decimal the server's per-animal
 * P&L uses, not a reimplementation that happens to agree. A partner deciding
 * whether to take an offer is reading this figure, so it has to be the figure.
 */
function profitAgainstCost(salePrice: string, totalCost: string): string | null {
  if (!DECIMAL_DRAFT.test(salePrice)) return null;
  return toMoneyString(toMoney(salePrice).minus(toMoney(totalCost)));
}

export function AnimalSaleForm({
  animalId,
  tag,
  totalCost,
}: {
  animalId: number;
  tag: string;
  totalCost: string;
}): JSX.Element {
  const { t } = useTranslation();

  const { data: accounts = [] } = useAccounts();
  const { data: customers = [] } = useCustomers();
  const sell = useRecordAnimalSale();

  /** Cash today, or a debt the buyer settles later (BR-24, D-019). */
  const [onCredit, setOnCredit] = useState(false);
  const [salePrice, setSalePrice] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [accountId, setAccountId] = useState<number | null>(null);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [date, setDate] = useState(todayInDhaka());
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isOpen, setIsOpen] = useState(false);

  const defaultAccount = accounts.find((account) => account.type === 'cash') ?? accounts[0];
  const selectedAccount = accountId ?? defaultAccount?.id ?? null;

  const profit = profitAgainstCost(salePrice, totalCost);

  /*
   * A credit sale must name the buyer, because the debt has to belong to
   * somebody — the server refuses a sale that names neither an account nor a
   * customer, and offering Save without one would be an invitation to be told
   * no.
   */
  const canSubmit = profit !== null && (onCredit ? customerId !== null : selectedAccount !== null) && !sell.isPending;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrorKey(null);
    setFieldErrors({});

    try {
      await sell.mutateAsync({
        animalId,
        date,
        salePrice,
        // No account on a credit sale: no cash moved, so naming one would put
        // money into a balance the partners could not find.
        ...(onCredit ? {} : { accountId: selectedAccount! }),
        ...(customerId !== null ? { customerId } : {}),
        ...(DECIMAL_DRAFT.test(weightKg) ? { weightKg } : {}),
      });
      // Nothing is cleared: the profile reloads with the animal sold, and this
      // form goes with it.
    } catch (error) {
      setErrorKey(errorMessageKey(error));
      setFieldErrors(errorFields(error) ?? {});
    }
  }

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="mt-4 min-h-touch w-full rounded-xl bg-green-700 px-4 py-3 text-sm font-medium text-white"
      >
        {t('animals.sale.open', { tag })}
      </button>
    );
  }

  return (
    <form className="mt-4 rounded-xl bg-gray-50 p-4" onSubmit={(event) => void handleSubmit(event)} noValidate>
      <h3 className="mb-3 text-base font-semibold text-gray-800">{t('animals.sale.title', { tag })}</h3>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="sale-price">
            {t('animals.sale.price')}
          </label>
          <input
            id="sale-price"
            inputMode="decimal"
            value={salePrice}
            onChange={(event) => setSalePrice(event.target.value)}
            placeholder="0.00"
            className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-3 tabular-nums"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="sale-weight">
            {t('animals.sale.weight')}
          </label>
          <input
            id="sale-weight"
            inputMode="decimal"
            value={weightKg}
            onChange={(event) => setWeightKg(event.target.value)}
            placeholder="0.00"
            className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-3 tabular-nums"
          />
        </div>
      </div>

      {fieldErrors['salePrice'] !== undefined && (
        <p className="mt-1 text-sm text-red-700">{fieldErrors['salePrice']}</p>
      )}

      {/*
        Profit against what the animal actually cost (BR-16). Shown before the
        sale rather than in a report afterwards, because this is the number a
        partner needs while deciding whether to take the offer in front of them.
      */}
      <div aria-live="polite" className="mt-3 rounded-xl bg-white px-4 py-3 shadow-sm">
        <p className="text-xs text-gray-500">{t('animals.sale.costSoFar', { amount: formatMoney(totalCost) })}</p>
        <p
          className={`mt-1 text-right text-2xl font-semibold tabular-nums ${
            profit === null ? 'text-gray-400' : isNegativeAmount(profit) ? 'text-red-700' : 'text-green-700'
          }`}
        >
          {profit === null
            ? '—'
            : isNegativeAmount(profit)
              ? t('animals.sale.loss', { amount: formatMoney(profit.replace('-', '')) })
              : t('animals.sale.profit', { amount: formatMoney(profit) })}
        </p>
      </div>

      <label className="mb-1 mt-3 block text-sm font-medium text-gray-700" htmlFor="sale-date">
        {t('animals.sale.date')}
      </label>
      <input
        id="sale-date"
        type="date"
        value={date}
        max={todayInDhaka()}
        onChange={(event) => setDate(event.target.value)}
        className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-3"
      />
      {fieldErrors['date'] !== undefined && <p className="mt-1 text-sm text-red-700">{fieldErrors['date']}</p>}

      <fieldset className="mt-4">
        <legend className="mb-2 text-sm font-medium text-gray-700">{t('animals.sale.howPaid')}</legend>
        <div className="grid grid-cols-2 gap-2">
          {[false, true].map((credit) => (
            <button
              key={String(credit)}
              type="button"
              onClick={() => setOnCredit(credit)}
              aria-pressed={onCredit === credit}
              className={`min-h-touch rounded-xl px-3 py-3 text-sm font-medium ${
                onCredit === credit ? 'bg-green-700 text-white' : 'bg-white text-gray-700 shadow-sm'
              }`}
            >
              {t(credit ? 'animals.sale.credit' : 'animals.sale.cash')}
            </button>
          ))}
        </div>
      </fieldset>

      {onCredit ? (
        <p className="mt-3 rounded-lg bg-amber-50 px-4 py-3 text-xs text-amber-900">{t('animals.sale.creditNote')}</p>
      ) : (
        <fieldset className="mt-4">
          <legend className="mb-2 text-sm font-medium text-gray-700">{t('animals.sale.account')}</legend>
          <div className="flex flex-wrap gap-2">
            {accounts.map((account) => (
              <button
                key={account.id}
                type="button"
                onClick={() => setAccountId(account.id)}
                aria-pressed={selectedAccount === account.id}
                className={`min-h-touch rounded-full px-4 py-2 text-sm ${
                  selectedAccount === account.id
                    ? 'bg-green-700 font-medium text-white'
                    : 'bg-white text-gray-700 shadow-sm'
                }`}
              >
                {account.name}
              </button>
            ))}
          </div>
          {fieldErrors['accountId'] !== undefined && (
            <p className="mt-1 text-sm text-red-700">{fieldErrors['accountId']}</p>
          )}
        </fieldset>
      )}

      {customers.length > 0 && (
        <fieldset className="mt-4">
          <legend className="mb-2 text-sm font-medium text-gray-700">
            {t(onCredit ? 'animals.sale.buyerRequired' : 'animals.sale.buyer')}
          </legend>
          <div className="flex flex-wrap gap-2">
            {customers
              .filter((customer) => customer.isActive)
              .map((customer) => (
                <button
                  key={customer.id}
                  type="button"
                  onClick={() => setCustomerId(customerId === customer.id ? null : customer.id)}
                  aria-pressed={customerId === customer.id}
                  className={`min-h-touch rounded-full px-4 py-2 text-sm ${
                    customerId === customer.id
                      ? 'bg-green-700 font-medium text-white'
                      : 'bg-white text-gray-700 shadow-sm'
                  }`}
                >
                  {customer.name}
                </button>
              ))}
          </div>
        </fieldset>
      )}

      {/* The animal leaves the herd now; the money still needs a second
          partner. Saying so stops "sold" being read as "paid for". */}
      <p className="mt-4 rounded-lg bg-gray-100 px-4 py-3 text-xs text-gray-600">{t('animals.sale.pendingNote')}</p>

      {errorKey !== null && (
        <div role="alert" className="mt-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p>{t(errorKey)}</p>
        </div>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="min-h-[52px] flex-1 rounded-xl bg-green-700 text-base font-medium text-white disabled:opacity-50"
        >
          {t(sell.isPending ? 'animals.sale.saving' : 'animals.sale.submit')}
        </button>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="min-h-touch rounded-xl bg-white px-4 py-2 text-sm text-gray-700"
        >
          {t('app.cancel')}
        </button>
      </div>
    </form>
  );
}
